import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Send } from 'lucide-react';
import * as Icons from 'lucide-react';
import { practiceAreas, sectorExpertise, globalRequests } from '../data/mock';
import { RequestDialog } from '../components/RequestDialog';

export const PracticeAreas = () => {
  const [isRequestOpen, setIsRequestOpen] = React.useState(false);
  const [activeConfig, setActiveConfig] = React.useState({});

  const handleConsultation = (source = "General") => {
    setActiveConfig(globalRequests.consultation);
    setIsRequestOpen(true);
  };

  const getIcon = (iconName, size = 32) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon size={size} className="text-gold" /> : null;
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      <RequestDialog
        isOpen={isRequestOpen}
        onOpenChange={setIsRequestOpen}
        config={activeConfig}
        context="Practice Areas Page"
      />
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* <div className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
            <span className="text-gold text-sm font-medium uppercase tracking-wide">Our Expertise</span>
          </div> */}

          <h1 className="text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
            Practice <span className="text-gold">Areas</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Comprehensive legal advisory across the corporate lifecycle - from early-stage ventures
            to complex multi-jurisdictional transactions. Our expertise spans critical practice areas
            that matter most to growing businesses and institutional clients.
          </p>
        </div>
      </section>

      {/* Practice Areas Grid */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {practiceAreas.map((area, index) => (
              <div
                key={area.id}
                className="group relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-2xl p-8 lg:p-10 hover:border-gold/50 transition-all duration-300 hover:transform hover:scale-[1.02]"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Background gradient effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-gold/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                    {getIcon(area.icon)}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-gold transition-colors duration-300">
                    {area.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 mb-6 leading-relaxed text-lg">
                    {area.description}
                  </p>

                  {/* Highlights */}
                  <div className="space-y-3">
                    <h4 className="text-gold font-semibold text-sm uppercase tracking-wider mb-4">
                      Key Services
                    </h4>
                    <ul className="space-y-3">
                      {area.highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-start text-gray-300">
                          <CheckCircle size={20} className="text-gold mr-3 mt-0.5 flex-shrink-0" />
                          <span className="leading-relaxed">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Corner decoration - Soft Glow */}
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-gold/10 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/*Sectoral Expertise Section*/}
      <section className='py-20 bg-gradient-to-b from-black to-gray-900'>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              Sector <span className="text-gold">Expertise</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Deep domain knowledge across traditional and emerging sectors of the new economy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectorExpertise.map((item, index) => (
              <div
                key={item.id}
                className="group relative bg-black/40 backdrop-blur-sm border border-gold/10 rounded-xl p-6 hover:border-gold/40 transition-all duration-300"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                    {getIcon(item.icon, 24)}
                  </div>
                  <h3 className="text-lg font-semibold text-white group-hover:text-gold transition-colors duration-300">
                    {item.sector}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      {/* <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              Why Choose <span className="text-gold">LEXCEED</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="text-4xl font-bold text-gold">10+</div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Years of Excellence</h3>
              <p className="text-gray-400">
                Deep expertise built through advising on complex transactions across multiple jurisdictions
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="text-4xl font-bold text-gold">360°</div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Comprehensive Advisory</h3>
              <p className="text-gray-400">
                End-to-end legal support from ideation to execution, covering all aspects of your business
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="text-4xl font-bold text-gold">24h</div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Responsive Counsel</h3>
              <p className="text-gray-400">
                Boutique agility means faster turnaround and dedicated attention to your matters
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
            Ready to Get <span className="text-gold">Started</span>?
          </h2>

          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Whether you're closing a critical deal, raising capital, or need ongoing corporate counsel—
            let's discuss how LEXCEED can support your goals.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={handleConsultation}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-gold to-gold-light text-black font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-gold/40 transform hover:scale-105 transition-all duration-300"
            >
              Schedule Consultation
              <Send size={20} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
            </button>
            <Link
              to="/solutions"
              className="inline-flex items-center px-8 py-4 border-2 border-gold text-gold font-semibold text-lg rounded-lg hover:bg-gold hover:text-black transition-all duration-300"
            >
              View Our Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
